import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import MapboxDraw, {
  MapboxDrawControls,
  DrawCreateEvent,
  DrawUpdateEvent,
  DrawDeleteEvent,
} from '@mapbox/mapbox-gl-draw';
import { useEffect, useMemo } from 'react';
import { useControl, useMap } from 'react-map-gl';

/* eslint-disable-next-line */
export interface DrawControlProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  limit?: number;
  onLoad?: (draw: MapboxDraw) => void;
  onCreate?: (e: DrawCreateEvent) => void;
  onUpdate?: (e: DrawUpdateEvent) => void;
  onDelete?: (e: DrawDeleteEvent) => void;
  initialPolygon?: GeoJSON.Feature<GeoJSON.MultiPolygon>;
  controls?: MapboxDrawControls;
}

export function DrawControl(props: DrawControlProps) {
  const { current: map } = useMap();
  const draw = useMemo(
    () =>
      new MapboxDraw({
        controls: {
          point: false,
          line_string: false,
          combine_features: false,
          uncombine_features: false,
          ...props.controls,
        },
        snapOptions: {},
      }),
    []
  );

  useControl(() => draw as any, {
    position: props.position,
  });

  useEffect(() => {
    props.onLoad?.(draw);
  }, [props.onLoad]);

  useEffect(() => {
    const onDrawCreate = (e: DrawCreateEvent) => {
      if (props.limit && draw.getAll().features.length > props.limit) {
        draw.delete(e.features[0].id as string);
      } else {
        props.onCreate?.(e);
      }
    };
    map?.on('draw.create', onDrawCreate);

    const onDrawUpdate = (e: DrawUpdateEvent) => {
      props.onUpdate?.(e);
    };
    map?.on('draw.update', onDrawUpdate);

    const onDrawDelete = (e: DrawDeleteEvent) => {
      props.onDelete?.(e);
    };
    map?.on('draw.delete', onDrawDelete);

    return () => {
      map?.off('draw.create', onDrawCreate);
      map?.off('draw.update', onDrawUpdate);
      map?.off('draw.delete', onDrawDelete);
    };
  }, [props]);

  useEffect(() => {
    if (props.initialPolygon) {
      draw.add(props.initialPolygon);
    }
  }, [props.initialPolygon]);

  return null;
}

export default DrawControl;
